import React from 'react';

import { useAppContext } from '@contexts/AppContext';
import useCreateLead from '@hooks/useCreateLead';
import { Theme } from '@interfaces/global';
import { ClickTarget, TypingInput } from '@tracking';
import { DEFAULT_ORGANIC_LEAD_TYPE, LeadDetails } from '@utils/backend-api/user';
import { FormLocation } from '@utils/enums';

import EmailForm from '../Form/EmailForm';

interface LockedLeadCaptureProps {
  articleId?: number;
  briefingGuid?: string;
  buttonClassName?: string;
  buttonText: string;
  forceEmailAuth?: boolean;
  formClassName?: string;
  formLocation: FormLocation;
  inputClassName?: string;
  includeSignInOption?: boolean;
  isGdprLead?: boolean;
  isRight?: boolean;
  isPro?: boolean;
  isVertical?: boolean;
  leadDetails?: LeadDetails;
  leadType?: string;
  newsletterIds?: number[];
  redirectUri: string;
  shareGuid?: string;
  signInOptionClass?: string;
  submitLink?: string;
  gdprClassName?: string;
  theme?: Theme;
  trackingProps?: { [key: string]: string };
  unlockContent?: boolean;
}

export interface EmailLeadForm {
  email: string;
  gdprOptIn?: boolean;
}

const EmailLeadCapture = ({
  articleId,
  briefingGuid,
  buttonClassName,
  buttonText,
  forceEmailAuth,
  formClassName,
  formLocation,
  inputClassName,
  includeSignInOption = false,
  isVertical = false,
  isRight = false,
  isPro = false,
  leadDetails,
  leadType = DEFAULT_ORGANIC_LEAD_TYPE,
  newsletterIds,
  redirectUri,
  shareGuid,
  signInOptionClass,
  gdprClassName,
  theme = Theme.Light,
  unlockContent = false,
}: LockedLeadCaptureProps) => {
  const { mutateAsync: createLead, isLoading } = useCreateLead(unlockContent, leadDetails);
  const { isGdprRequired } = useAppContext();

  const handleSubmit = ({ email, gdprOptIn }: EmailLeadForm) => {
    const createUserRequestParams = {
      article_id: articleId,
      briefing_guid: briefingGuid,
      force_email_auth: forceEmailAuth,
      newsletter_ids: newsletterIds,
      redirect_to_on_success: redirectUri,
      share_guid: shareGuid,
      user: {
        email: email,
        declined_opt_in: isGdprRequired ? !gdprOptIn : false,
        opt_in_required: isGdprRequired,
        lead_type: leadType,
        form_location: formLocation,
        page: window.location.href,
      },
    };
    return createLead(createUserRequestParams);
  };

  return (
    <EmailForm
      buttonText={buttonText}
      formClassName={formClassName}
      gdprClassName={gdprClassName}
      theme={theme}
      isVertical={isVertical}
      onSubmit={handleSubmit}
      isProcessingSubmission={isLoading}
      inputClassName={inputClassName}
      buttonClassName={buttonClassName}
      isPro={isPro}
      isRight={isRight}
      includeSignInOption={includeSignInOption}
      signInOptionClass={signInOptionClass}
      trackingClickTarget={ClickTarget.LeadCapture}
      trackingInputTarget={TypingInput.LeadCapture}
    />
  );
};

export default EmailLeadCapture;
